/**
 * Lip-sync 文字录入：根据字符数估算口播时长。
 * - 中文（Unicode Han）：5 字符/秒
 * - 其它字符：10 字符/秒
 * 混合文本时对两类字符分别计数后，先加总再向上取整为整秒。
 */

const CHINESE_CHARS_PER_SECOND = 5;
const OTHER_CHARS_PER_SECOND = 10;

/** 是否为中文字符（CJK 统一表意文字，不含假名、谚文等） */
const isChineseHanChar = (char: string): boolean => {
  return /^\p{Script=Han}$/u.test(char);
};

export interface LipSyncTextCharCounts {
  chinese: number;
  other: number;
}

export function countLipSyncTextCharsByScript(text: string): LipSyncTextCharCounts {
  let chinese = 0;
  let other = 0;
  for (const char of text) {
    if (isChineseHanChar(char)) {
      chinese += 1;
    } else {
      other += 1;
    }
  }
  return { chinese, other };
}

/** 业务展示与上限：汉字计 2，其它字符计 1（与 {@link countLipSyncTextCharsByScript} 的 Han 判定一致） */
export function countLipSyncTextWeightedLength(text: string): number {
  const { chinese, other } = countLipSyncTextCharsByScript(text);
  return chinese * 2 + other;
}

/** 按加权长度截断，保证不超过 maxWeighted */
export function trimLipSyncTextToMaxWeightedLength(text: string, maxWeighted: number): string {
  let weighted = 0;
  let result = '';
  for (const char of text) {
    const w = isChineseHanChar(char) ? 2 : 1;
    if (weighted + w > maxWeighted) break;
    weighted += w;
    result += char;
  }
  return result;
}

/** 根据文本内容估算口播时长；传入边界时保持旧调用方的归一化行为。 */
export function estimateLipSyncTextDurationSeconds(text: string, minSeconds?: number, maxSeconds?: number): number {
  const { chinese, other } = countLipSyncTextCharsByScript(text);
  const rawSeconds = chinese / CHINESE_CHARS_PER_SECOND + other / OTHER_CHARS_PER_SECOND;
  const estimatedSeconds = Math.ceil(rawSeconds);
  if (minSeconds === undefined || maxSeconds === undefined) return estimatedSeconds;

  return Math.min(maxSeconds, Math.max(minSeconds, estimatedSeconds));
}
